import { Archive } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { Badge, BadgeProps } from 'v5/design-system/Badge';
import { ColorDot } from 'v5/patterns/widgets/ColorDot';
import { cn } from 'v5/platform/dom/cn';

export type ResourceGroupBadgeProps = Omit<
  BadgeProps,
  'variant' | 'tone' | 'size' | 'asChild'
> & {
  label: ReactNode;
  colour?: string | null;
  archived?: boolean;
  readOnly?: boolean;
};

/** Shared v5 presentation for assigned groups; children can supply a removal control. */
export const ResourceGroupBadge = forwardRef<
  HTMLDivElement,
  ResourceGroupBadgeProps
>(
  (
    {
      label,
      colour,
      archived = false,
      readOnly,
      children,
      className,
      ...props
    },
    ref
  ) => (
    <Badge
      {...props}
      ref={ref}
      variant="gray"
      tone="subtle"
      size="sm"
      aria-readonly={readOnly}
      className={cn(
        'max-w-full text-sm whitespace-nowrap overflow-hidden border-0 px-[5px] py-[3px]',
        archived && 'text-fg-muted',
        className
      )}
    >
      {archived ? (
        <Archive className="size-3 shrink-0" aria-hidden />
      ) : (
        <ColorDot color={colour} aria-hidden />
      )}
      <span className={cn('truncate', archived && 'line-through')}>
        {label}
      </span>
      {children}
    </Badge>
  )
);

ResourceGroupBadge.displayName = 'ResourceGroupBadge';
